/**
 * @file AlbuminuriaF13ListView.jsx
 * @author Fernando Ruano
 * @description Albuminuria F13 List View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

// Helpers
import {
  formatDate,
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';
import {
  createColumns,
  PAGE_LIMIT,
  getSearchApiKeys,
  getSorterString,
  getSelectFilter,
  setBadgeVariation
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function AlbuminuriaF13ListView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { shift, totalItems: totalItemsParams } = useParams();
  const [totalItems, setTotalItems] = React.useState(totalItemsParams);
  const [dataFilter, setDataFilter] = React.useState(null);
  const [columns, setColumns] = React.useState([]);
  const [filter, setFilter] = React.useState(null);
  const [sorter, setSorter] = React.useState(null);

  const { getAlbuminuriaF13ListPlus } = dashboardStaffActions;

  const additionalColumns = [
    {
      title: translate('Última albuminuria 24 hr o RAC'),
      dataIndex: 'ultima_albuminuria',
      filter: {
        listName: 'lista_ultima_albuminuria',
        searchName: 'filtroUltimaAlbuminuria',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('ultima_albuminuria'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_ultima_albuminuria',
            searchName: 'filtroUltimaAlbuminuria'
          },
          dataIndex: 'ultima_albuminuria'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('Fecha última albuminuria 24 hr o RAC'),
      dataIndex: 'fecha_ultima_albuminuria',
      filter: {
        listName: 'lista_fecha_ultima_albuminuria',
        searchName: 'filtroFechaUltimaAlbuminuria',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_ultima_albuminuria'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_fecha_ultima_albuminuria',
            searchName: 'filtroFechaUltimaAlbuminuria'
          },
          dataIndex: 'fecha_ultima_albuminuria'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        controlSeleccionado: shift,
        idClinic: clinicId,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };
    getAlbuminuriaF13ListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        setTotalItems(resp.data?.totalRegistros);

        const newRowData = resp?.data?.listaDetalles?.map((item) => {
          return {
            ...item,
            fecha_ultima_albuminuria: item?.fecha_ultima_albuminuria
              ? formatDate(item.fecha_ultima_albuminuria)
              : '',
            fecha_penultima_albuminuria: item?.fecha_penultima_albuminuria
              ? formatDate(item.fecha_penultima_albuminuria)
              : ''
          };
        });
        setRowData(setBadgeVariation(newRowData ?? [], translate));
        setDataFilter(resp.data);
      }
    });
  };

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  React.useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  React.useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={parseInt(totalItems, 10)}
      additionalColumns={additionalColumns}
      onPaginationChange={getetData}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
AlbuminuriaF13ListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getAlbuminuriaF13ListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default AlbuminuriaF13ListView;
